/**
 * Communities Table Component
 * Displays communities in a table with actions
 */

import { useState, useMemo } from 'react';
import { FileQuestion, Pencil, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { StatusBadge } from '../atoms/StatusBadge';
import { formatDiscount, formatDate, formatStrategyType, normalizeStrategyType } from '../../utils/format-helpers';
import type { CommunityProfile } from '../../types/communities-types';

interface Actor {
  actorId: number;
  name?: string;
}

interface CommunitiesTableProps {
  communities: CommunityProfile[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];
  isLoading: boolean;
  error: string | null;
  actors?: Actor[];
  onEdit: (community: CommunityProfile) => void;
  onDelete: (id: number) => void;
  onAddNew: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

type SortColumn = 'name' | 'description' | 'location' | 'strategy' | 'discount' | 'status' | 'startDate' | 'assignedAccounts';

export function CommunitiesTable({
  communities,
  totalCount,
  currentPage,
  pageSize,
  pageSizeOptions,
  isLoading,
  error,
  actors = [],
  onEdit,
  onDelete,
  onAddNew,
  onPageChange,
  onPageSizeChange,
}: CommunitiesTableProps) {

  // Sorting state - track column and direction
  const [sortConfig, setSortConfig] = useState<{ column: SortColumn; direction: 'asc' | 'desc' } | null>(null);

  // Sort communities by selected column
  const sortedCommunities = useMemo(() => {
    if (!sortConfig || !communities) return communities;

    return [...communities].sort((a, b) => {
      let compareResult = 0;

      switch (sortConfig.column) {
        case 'name':
          compareResult = (a.profileName?.toLowerCase() || '').localeCompare(
            b.profileName?.toLowerCase() || ''
          );
          break;
        case 'description':
          compareResult = (a.profileDescription?.toLowerCase() || '').localeCompare(
            b.profileDescription?.toLowerCase() || ''
          );
          break;
        case 'location': {
          const locationA = actors.find(actor => actor.actorId === a.actorId)?.name?.toLowerCase() || '';
          const locationB = actors.find(actor => actor.actorId === b.actorId)?.name?.toLowerCase() || '';
          compareResult = locationA.localeCompare(locationB);
          break;
        }
        case 'strategy':
          compareResult = normalizeStrategyType(a.strategyType) - normalizeStrategyType(b.strategyType);
          break;
        case 'discount':
          compareResult = (a.singleDimensionValue || 0) - (b.singleDimensionValue || 0);
          break;
        case 'status':
          compareResult = (a.profileStatus?.toLowerCase() || '').localeCompare(
            b.profileStatus?.toLowerCase() || ''
          );
          break;
        case 'startDate': {
          const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
          const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
          compareResult = dateA - dateB;
          break;
        }
        case 'assignedAccounts':
          compareResult = (a.assignedAccounts || 0) - (b.assignedAccounts || 0);
          break;
      }

      return sortConfig.direction === 'asc' ? compareResult : -compareResult;
    });
  }, [communities, sortConfig]);

  // Toggle sort for a column
  const handleSort = (column: SortColumn) => {
    setSortConfig((current) => {
      if (!current || current.column !== column) {
        return { column, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return { column, direction: 'asc' };
    });
  };

  // Render sortable header
  const renderSortableHeader = (column: SortColumn, label: string, align: 'left' | 'center' | 'right' = 'left') => {
    const isActive = sortConfig?.column === column;
    const direction = isActive ? sortConfig.direction : null;

    const justifyClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

    return (
      <div
        className={`flex items-center ${justifyClass} space-x-1 cursor-pointer select-none hover:text-[#434343]`}
        onClick={() => handleSort(column)}
      >
        <span>{label}</span>
        <div className="flex flex-col">
          <ChevronUp
            className={`h-3 w-3 ${
              direction === 'asc' ? 'text-[#FFCD00]' : 'text-gray-400'
            }`}
          />
          <ChevronDown
            className={`h-3 w-3 -mt-1 ${
              direction === 'desc' ? 'text-[#FFCD00]' : 'text-gray-400'
            }`}
          />
        </div>
      </div>
    );
  };

  // Calculate pagination info (matching Kiosk Reports)
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Generate page numbers to show (matching Kiosk Reports: shows up to 4 pages)
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;

    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };
  // Handle error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-medium">Error loading communities</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  // Handle empty state
  if (!isLoading && (!communities || communities.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileQuestion className="h-12 w-12 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No communities configured</h3>
        <p className="text-sm text-gray-500 mb-4">
          Create your first community pricing profile
        </p>
        <Button onClick={onAddNew}>
          <span className="mr-2">+</span>
          Add Community
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Table with fixed header and scrollable body */}
      <div className="flex-1 overflow-auto rounded-md border border-[#E8E8E8] bg-white scrollbar-visible">
        <div className="relative w-full">
          <table className="w-full caption-bottom text-sm">
            <thead className="sticky top-0 z-10 bg-[#F1F1F1] [&_tr]:border-b">
              <tr className="border-b bg-[#F1F1F1]">
                <th className="h-12 px-4 text-left align-middle text-xs font-medium text-[#262626] min-w-[150px]">
                  {renderSortableHeader('name', 'Name', 'left')}
                </th>
                <th className="h-12 px-4 text-left align-middle text-xs font-medium text-[#262626] hidden md:table-cell min-w-[200px]">
                  {renderSortableHeader('description', 'Description', 'left')}
                </th>
                <th className="h-12 px-4 text-left align-middle text-xs font-medium text-[#262626] min-w-[150px]">
                  {renderSortableHeader('location', 'Location', 'left')}
                </th>
                <th className="h-12 px-4 text-left align-middle text-xs font-medium text-[#262626] min-w-[120px]">
                  {renderSortableHeader('strategy', 'Discount Type', 'left')}
                </th>
                <th className="h-12 px-4 text-center align-middle text-xs font-medium text-[#262626] min-w-[100px]">
                  {renderSortableHeader('discount', 'Discount', 'center')}
                </th>
                <th className="h-12 px-4 text-left align-middle text-xs font-medium text-[#262626] hidden lg:table-cell min-w-[120px]">
                  {renderSortableHeader('startDate', 'Start Date', 'left')}
                </th>
                <th className="h-12 px-4 text-center align-middle text-xs font-medium text-[#262626] min-w-[100px]">
                  {renderSortableHeader('status', 'Status', 'center')}
                </th>
                <th className="h-12 px-4 text-center align-middle text-xs font-medium text-[#262626] min-w-[120px]">
                  {renderSortableHeader('assignedAccounts', 'Total Accounts', 'center')}
                </th>
                <th className="h-12 px-4 text-left align-middle text-xs font-medium text-[#262626] min-w-[100px]">Actions</th>
              </tr>
            </thead>

            {isLoading ? (
              <tbody className="[&_tr:last-child]:border-0">
                {Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-4 align-middle"><Skeleton className="h-4 w-32" /></td>
                    <td className="p-4 align-middle"><Skeleton className="h-4 w-48" /></td>
                    <td className="p-4 align-middle"><Skeleton className="h-5 w-28" /></td>
                    <td className="p-4 align-middle"><Skeleton className="h-5 w-24" /></td>
                    <td className="p-4 align-middle"><Skeleton className="h-4 w-16" /></td>
                    <td className="p-4 align-middle"><Skeleton className="h-4 w-24" /></td>
                    <td className="p-4 align-middle"><Skeleton className="h-5 w-20" /></td>
                    <td className="p-4 align-middle"><Skeleton className="h-4 w-16" /></td>
                    <td className="p-4 align-middle">
                      <div className="flex gap-2">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody className="[&_tr:last-child]:border-0">
                {sortedCommunities?.map((community, index) => (
                  <tr
                    key={`${community.profileId}-${index}`}
                    className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted cursor-pointer"
                    onDoubleClick={() => onEdit(community)}
                  >
                    <td className="p-4 align-middle font-medium">{community.profileName}</td>
                    <td className="p-4 align-middle hidden md:table-cell">
                      {community.profileDescription.length > 50
                        ? `${community.profileDescription.substring(0, 50)}...`
                        : community.profileDescription}
                    </td>
                    <td className="p-4 align-middle">
                      <span className="text-sm">
                        {actors.find(actor => actor.actorId === community.actorId)?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="p-4 align-middle">
                      <span className="text-sm">{formatStrategyType(community.strategyType)}</span>
                    </td>
                    <td className="p-4 align-middle text-center">
                      {formatDiscount(community.singleDimensionValue, community.strategyType)}
                    </td>
                    <td className="p-4 align-middle hidden lg:table-cell">
                      {formatDate(community.startDate)}
                    </td>
                    <td className="p-4 align-middle text-center">
                      <StatusBadge status={community.profileStatus} />
                    </td>
                    <td className="p-4 align-middle text-center">
                      <span className="text-sm font-medium">{community.assignedAccounts ?? 0}</span>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex justify-start gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(community)}
                          title="Edit community"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(community.profileId)}
                          title="Delete community"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>

      {/* Pagination Footer - Matching Kiosk Reports style */}
      {totalCount > 0 && (
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-x border-b border-[#E8E8E8] bg-white rounded-b-md">
          {/* Left side - Page navigation */}
          <div className="flex items-center gap-2">
            {/* First Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1 || isLoading}
              className="h-8 w-8 p-0 border-[#E8E8E8]"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>

            {/* Previous Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="h-8 w-8 p-0 border-[#E8E8E8]"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Page Numbers */}
            {getPageNumbers().map((pageNum) => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                disabled={isLoading}
                className={`h-8 w-8 p-0 ${
                  currentPage === pageNum
                    ? "bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] border-[#FFCD00]"
                    : "border-[#E8E8E8]"
                }`}
              >
                {pageNum}
              </Button>
            ))}

            {/* Next Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              className="h-8 w-8 p-0 border-[#E8E8E8]"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            {/* Last Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages || isLoading}
              className="h-8 w-8 p-0 border-[#E8E8E8]"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>

            {/* Page Info */}
            <span className="text-sm text-[#606060] ml-2">
              {currentPage} of {totalPages} pages
            </span>
          </div>

          {/* Right side - Range info and page size selector */}
          <div className="flex items-center gap-4">
            {/* Range Info */}
            <span className="text-sm text-[#606060]">
              {startItem}-{endItem} of {totalCount.toLocaleString()}
            </span>

            {/* Rows per page */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#606060]">Rows per page:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
                disabled={isLoading}
              >
                <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
